import type { History } from "history";
import { createBrowserHistory } from "history";
import type { ReactNode } from "react";
import { useEffect } from "react";

import { HistoryProvider } from "@shared/context/HistoryContext";
import { NavigationHistoryProvider } from "@shared/context/NavigationHistoryContext";
import type {
  HistoryRouterProps,
  BrowserRouterProps,
} from "@shared/react-router-dom/react-router-dom";
import { unstable_HistoryRouter as Router } from "@shared/react-router-dom/react-router-dom";
import { attachRemixHandoff } from "@shared/utils/remixOwnedPaths/attachRemixHandoff";

interface Props extends BrowserRouterProps {
  history?: History;
  children: ReactNode;
  isRemixHandoffEnabled?: boolean;
}

const BrowserRouter = ({
  children,
  history: initialHistory,
  isRemixHandoffEnabled = false,
  ...props
}: Props) => {
  const history = initialHistory ?? createBrowserHistory(props);

  useEffect(() => {
    const unlisten = history.listen(({ location }) => {
      if (typeof window.ga === "function") {
        window.ga("set", "page", location.pathname + location.search);
        window.ga("send", "pageview");
      }

      window.dataLayer?.push({ event: "pageview" });
    });

    // Only attach the Rails → Remix handoff when this component owns the
    // history it created. Callers that pass their own `history` (clinic and
    // clerk, via `clinicBrowserHistory`) already attach the handoff
    // themselves, so attaching it here too would fire the document
    // navigation twice. The handoff is also gated on the
    // `hopper_remix_prac_routing` kill-switch, which consumers pass down via
    // `isRemixHandoffEnabled`; it defaults off so a consumer that hasn't
    // opted in never hands off (when off, nginx serves Rails, so handing off
    // would trigger a redundant document load).
    const unlistenRemixHandoff =
      initialHistory || !isRemixHandoffEnabled ? undefined : attachRemixHandoff(history);

    return () => {
      unlisten();
      unlistenRemixHandoff?.();
    };
  }, []);

  return (
    // Unfortunately the only way to silence the type errors here unless we ts-ignore is to do this
    // see https://github.com/remix-run/react-router/issues/9422#issuecomment-1301954272
    <Router {...({ ...props, history } as unknown as HistoryRouterProps)}>
      <NavigationHistoryProvider>
        <HistoryProvider history={history}>{children}</HistoryProvider>
      </NavigationHistoryProvider>
    </Router>
  );
};

export { BrowserRouter };
